import React, { useState, useEffect } from 'react';
import {
  Phone, Bell, Mic, Volume2, Shield, Bot,
  Save, RefreshCw, Check, X, Sparkles,
  Radio, Wifi, Settings, Zap, Brain, PhoneCall
} from 'lucide-react';
import { voipService } from '../../services/voipService';

const VoipSettings = ({ extension, onUpdate }) => {
  const [settings, setSettings] = useState({
    voicemail_enabled: extension.voicemail_enabled,
    voicemail_email_notification: extension.voicemail_email_notification,
    voicemail_transcription: extension.voicemail_transcription,
    call_recording_enabled: extension.call_recording_enabled,
    forward_on_busy: extension.forward_on_busy || '',
    forward_on_no_answer: extension.forward_on_no_answer || '',
    forward_timeout: extension.forward_timeout || 20,
    astraea_screening_enabled: extension.astraea_screening_enabled,
    astraea_answer_enabled: extension.astraea_answer_enabled,
    astraea_answer_rings: extension.astraea_answer_rings || 4
  });

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const handleSave = async () => {
    try {
      setSaving(true);
      await voipService.updateExtension(extension.id, settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Phone },
    { id: 'voicemail', label: 'Voicemail', icon: Bell },
    { id: 'forwarding', label: 'Forwarding', icon: RefreshCw },
    { id: 'astraea', label: 'Astraea AI', icon: Brain }
  ];

  return (
    <div className="h-full bg-gradient-to-br from-slate-900 via-slate-900/30 to-slate-900 rounded-xl p-6 overflow-auto">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-cyan-500/20 mb-6">
        <div className="p-6 border-b border-cyan-500/20">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Extension Settings</h2>
              <p className="text-cyan-400">
                Extension {extension.extension} - {extension.display_name}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex p-2 gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg shadow-cyan-500/25'
                  : 'text-cyan-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Settings Content */}
      <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-cyan-500/20 p-6">
        {/* General Tab */}
        {activeTab === 'general' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5 text-cyan-400" />
                General Settings
              </h3>

              {/* Line Status Indicator */}
              <div className="flex items-center justify-between py-4 px-4 bg-slate-700/30 rounded-xl border border-cyan-500/10 mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30">
                    <Wifi className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-medium text-white">Line Status</div>
                    <div className="text-sm text-cyan-400">
                      Your phone line is currently online
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                  <span className="text-green-400 font-medium">Online</span>
                </div>
              </div>

              {/* Call Recording */}
              <div className="flex items-center justify-between py-4 px-4 bg-slate-700/30 rounded-xl border border-cyan-500/10">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-rose-500 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/30">
                    <Mic className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-medium text-white">Call Recording</div>
                    <div className="text-sm text-cyan-400">
                      Automatically record all calls for quality and training purposes
                    </div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.call_recording_enabled}
                    onChange={(e) => handleChange('call_recording_enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-12 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-cyan-500 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-cyan-500 peer-checked:to-blue-500"></div>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Voicemail Tab */}
        {activeTab === 'voicemail' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-cyan-400" />
                Voicemail Settings
              </h3>

              {/* Voicemail Enabled */}
              <div className="flex items-center justify-between py-4 px-4 bg-slate-700/30 rounded-xl border border-cyan-500/10 mb-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
                    <Bell className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-medium text-white">Enable Voicemail</div>
                    <div className="text-sm text-cyan-400">
                      Allow callers to leave voicemail messages
                    </div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.voicemail_enabled}
                    onChange={(e) => handleChange('voicemail_enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-12 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-cyan-500 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-cyan-500 peer-checked:to-blue-500"></div>
                </label>
              </div>

              {/* Email Notifications */}
              <div className="flex items-center justify-between py-4 px-4 bg-slate-700/30 rounded-xl border border-cyan-500/10 mb-3">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${settings.voicemail_enabled ? 'bg-gradient-to-br from-blue-500 to-cyan-500 shadow-blue-500/30' : 'bg-slate-600'}`}>
                    <Bell className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className={`font-medium ${settings.voicemail_enabled ? 'text-white' : 'text-slate-500'}`}>Email Notifications</div>
                    <div className="text-sm text-cyan-400">
                      Receive email when you have a new voicemail
                    </div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.voicemail_email_notification}
                    onChange={(e) => handleChange('voicemail_email_notification', e.target.checked)}
                    disabled={!settings.voicemail_enabled}
                    className="sr-only peer"
                  />
                  <div className={`w-12 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-cyan-500 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-cyan-500 peer-checked:to-blue-500 ${!settings.voicemail_enabled ? 'opacity-50' : ''}`}></div>
                </label>
              </div>

              {/* Voicemail Transcription */}
              <div className="flex items-center justify-between py-4 px-4 bg-slate-700/30 rounded-xl border border-cyan-500/10">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${settings.voicemail_enabled ? 'bg-gradient-to-br from-violet-500 to-cyan-500 shadow-violet-500/30' : 'bg-slate-600'}`}>
                    <Volume2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className={`font-medium ${settings.voicemail_enabled ? 'text-white' : 'text-slate-500'}`}>Voicemail Transcription</div>
                    <div className="text-sm text-cyan-400">
                      Automatically transcribe voicemails to text
                    </div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.voicemail_transcription}
                    onChange={(e) => handleChange('voicemail_transcription', e.target.checked)}
                    disabled={!settings.voicemail_enabled}
                    className="sr-only peer"
                  />
                  <div className={`w-12 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-cyan-500 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-cyan-500 peer-checked:to-blue-500 ${!settings.voicemail_enabled ? 'opacity-50' : ''}`}></div>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Forwarding Tab */}
        {activeTab === 'forwarding' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-cyan-400" />
                Call Forwarding
              </h3>

              {/* Forward on Busy */}
              <div className="mb-6 p-4 bg-slate-700/30 rounded-xl border border-cyan-500/10">
                <label className="block text-sm font-medium text-cyan-300 mb-2">
                  Forward When Busy
                </label>
                <input
                  type="tel"
                  value={settings.forward_on_busy}
                  onChange={(e) => handleChange('forward_on_busy', e.target.value)}
                  placeholder="Enter phone number or extension"
                  className="w-full px-4 py-3 bg-slate-700/50 border border-cyan-500/30 rounded-xl text-white placeholder-cyan-400/50 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
                <p className="mt-2 text-sm text-cyan-400">
                  Forward calls to this number when your line is busy
                </p>
              </div>

              {/* Forward on No Answer */}
              <div className="mb-6 p-4 bg-slate-700/30 rounded-xl border border-cyan-500/10">
                <label className="block text-sm font-medium text-cyan-300 mb-2">
                  Forward When No Answer
                </label>
                <input
                  type="tel"
                  value={settings.forward_on_no_answer}
                  onChange={(e) => handleChange('forward_on_no_answer', e.target.value)}
                  placeholder="Enter phone number or extension"
                  className="w-full px-4 py-3 bg-slate-700/50 border border-cyan-500/30 rounded-xl text-white placeholder-cyan-400/50 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
                <p className="mt-2 text-sm text-cyan-400">
                  Forward calls to this number if you don't answer
                </p>
              </div>

              {/* Forward Timeout */}
              <div className="p-4 bg-slate-700/30 rounded-xl border border-cyan-500/10">
                <label className="block text-sm font-medium text-cyan-300 mb-2">
                  Forward Timeout (seconds)
                </label>
                <input
                  type="number"
                  min="10"
                  max="60"
                  value={settings.forward_timeout}
                  onChange={(e) => handleChange('forward_timeout', parseInt(e.target.value))}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-cyan-500/30 rounded-xl text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
                <p className="mt-2 text-sm text-cyan-400">
                  Number of seconds to ring before forwarding
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Astraea AI Tab */}
        {activeTab === 'astraea' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Brain className="w-5 h-5 text-cyan-400" />
                Astraea AI Settings
                <span className="px-2 py-0.5 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-300 text-xs rounded-full border border-cyan-500/30">
                  Next-Gen
                </span>
              </h3>

              {/* Call Screening */}
              <div className="flex items-center justify-between py-4 px-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-xl border border-cyan-500/20 mb-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-medium text-white flex items-center gap-2">
                      Call Screening
                      <Sparkles className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="text-sm text-cyan-300">
                      Astraea will screen incoming calls and identify the caller
                    </div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.astraea_screening_enabled}
                    onChange={(e) => handleChange('astraea_screening_enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-12 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-cyan-500 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-cyan-500 peer-checked:to-blue-500"></div>
                </label>
              </div>

              {/* Auto Answer */}
              <div className="flex items-center justify-between py-4 px-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-xl border border-cyan-500/20 mb-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/30">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-medium text-white flex items-center gap-2">
                      Auto Answer Calls
                      <Sparkles className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="text-sm text-cyan-300">
                      Allow Astraea to answer calls when you're unavailable
                    </div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.astraea_answer_enabled}
                    onChange={(e) => handleChange('astraea_answer_enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-12 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-cyan-500 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-cyan-500 peer-checked:to-blue-500"></div>
                </label>
              </div>

              {/* Answer Rings */}
              {settings.astraea_answer_enabled && (
                <div className="ml-4 mb-6 p-4 bg-slate-700/30 rounded-xl border border-cyan-500/10">
                  <label className="block text-sm font-medium text-cyan-300 mb-2">
                    Answer After Rings
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={settings.astraea_answer_rings}
                    onChange={(e) => handleChange('astraea_answer_rings', parseInt(e.target.value))}
                    className="w-full px-4 py-3 bg-slate-700/50 border border-cyan-500/30 rounded-xl text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                  <p className="mt-2 text-sm text-cyan-400">
                    Number of rings before Astraea answers the call
                  </p>
                </div>
              )}

              {/* AI Capabilities Info */}
              <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl p-6 mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="font-semibold text-white">What Astraea Can Do</h4>
                </div>
                <ul className="space-y-2">
                  {[
                    'Screen and identify callers',
                    'Take messages and gather information',
                    'Schedule appointments and callbacks',
                    'Route calls to appropriate team members',
                    'Answer common questions about your firm',
                    'Provide case status updates to clients'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-cyan-200">
                      <Zap className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex items-center justify-end gap-4 mt-8 pt-6 border-t border-cyan-500/20">
          {saved && (
            <div className="flex items-center gap-2 text-green-400">
              <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium">Settings saved</span>
            </div>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl hover:from-cyan-500 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg shadow-cyan-500/25"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoipSettings;
